import React, { memo, useCallback, useMemo, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { useFocusEffect, useScrollToTop } from '@react-navigation/native';
import Animated, { useAnimatedProps, useAnimatedRef, useAnimatedScrollHandler, useAnimatedStyle, useSharedValue, withTiming, } from 'react-native-reanimated';
import { AppView, Button, Spacer } from 'design-system';
import SectionHeader from 'design-system/SectionHeader';
import { rem, SCREEN_BOTTOM_PADDING } from 'design-system/values';
import { ExpandableFloatingBarSpacer } from 'design-system/ExpandableFloatingBar';
import useStyles from 'hooks/useStyles';
import useStatusBar from 'hooks/useStatusBar';
import { Portal } from 'utils/packages/portal';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { selectIsInvestWalkthroughActive } from 'reducers/selectors/walkthrough';
import InvestWalkthrough from 'features/feed/components/walkthrough/InvestWalkthrough';
import InvestHeaderOverlay from 'features/feed/components/walkthrough/invest/InvestHeaderOverlay';
import useInvestWalkthroughLayout from 'features/feed/components/walkthrough/useInvestWalkthroughLayout';
import IconPlus from 'design-system/icons/IconPlus';
import useColors from 'hooks/useColors';
import Modal from 'utils/packages/Modal';
import { AccountTypeDescription, AccountTypeDisplayLong } from '../constants';
import MigrationToSecclBanner from './MigrationToSecclBanner';
import ActiveTradingAccountCard from './ActiveTradingAccountCard';
import FSCSCompensationInfoView from './FSCSCompensationInfoView';
import { useGetBrokerImage } from '../utils/getInvestBrokerImage';
import TotalInvestmentsContainer from './TotalInvestmentsContainer';
import InitialInvestmentSection from './InitialInvestmentSection';
import { NavigationHeaderWithAvatar } from './NavigationHeaderWithAvatar';
import ComingSoonTradingAccountCard from './ComingSoonTradingAccountCard';
import { pauseOnboardingState, setSelectedTradingAccountId } from '../actions';
import { selectTradingAccountList, selectActiveSecclAccounts, selectHasTradedInAnySecclAccount, selectShouldTriggerInvestWalkthrough, } from '../reducers/selectors';
import InvestUsageExpandableFloatingBar from './InvestUsageExpandableFloatingComponent/InvestUsageExpandableFloatingBar';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import useNavigateToIntroducingInvestmentAccount from '../hooks/useNavigateToIntroducingInvestmentAccount';
import { blockInvestActionOnWeb } from '../webGate';
import { isWeb } from '../../../constants';
const MultiTradingAccountsView = () => {
    useStatusBar('dark-content', false);
    const dispatch = useAppDispatch();
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const navigateToIntroducingInvestmentAccount = useNavigateToIntroducingInvestmentAccount();
    const { isSecclGIAOperating, isSecclISAOperating, isAlpacaGIAAccountActive, accounts: tradingAccountList, isAlpacaAccountActiveOrClosed, } = useAppSelector(selectTradingAccountList);
    const navigateToNextScreen = useNavigateToNextScreen();
    const secclAccounts = useAppSelector(selectActiveSecclAccounts);
    const hasTradedInAnySecclAccount = useAppSelector(selectHasTradedInAnySecclAccount);
    const styles = useStyles(styleSet);
    useFocusEffect(useCallback(() => {
        // Coming back to the tab leaves the application in progress, so keep it to continue later
        dispatch(pauseOnboardingState());
        dispatch(setSelectedTradingAccountId(undefined));
    }, []));
    const shouldTriggerInvestWalkthrough = useAppSelector(selectShouldTriggerInvestWalkthrough);
    const didNavigateToWalkthroughRef = useRef(false);
    useFocusEffect(useCallback(() => {
        // The walkthrough points at the native tab bar, so web skips it
        if (shouldTriggerInvestWalkthrough && !didNavigateToWalkthroughRef.current && !isWeb) {
            navigation.navigate('InvestWalkthroughModal');
            didNavigateToWalkthroughRef.current = true;
        }
    }, [shouldTriggerInvestWalkthrough]));
    const getBrokerImage = useGetBrokerImage();
    const scrollY = useSharedValue(0);
    const { itemPositions, scrollHeight, scrollLayoutHeight, onContentSizeChange, onLayout, onScrollLayout } = useInvestWalkthroughLayout();
    const walkthroughInProgress = useAppSelector(selectIsInvestWalkthroughActive);
    const scrollRef = useAnimatedRef();
    useScrollToTop(scrollRef);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(true);
    const contentContainerStyle = useMemo(() => ({
        paddingBottom: SCREEN_BOTTOM_PADDING,
        ...paddingHorizontalStyle,
    }), [paddingHorizontalStyle]);
    const onTotalInvestmentsLayout = React.useCallback((e) => {
        onLayout('totalInvestments', e);
    }, [onLayout]);
    const onInvestmentAccountsLayout = React.useCallback((e) => {
        onLayout('investmentAccounts', e);
    }, [onLayout]);
    const handleOpenSecclAccount = useCallback(() => {
        navigateToIntroducingInvestmentAccount('GIA');
    }, [navigateToIntroducingInvestmentAccount]);
    const handleOpenSecclISAAccount = useCallback(() => {
        navigateToIntroducingInvestmentAccount('ISA');
    }, [navigateToIntroducingInvestmentAccount]);
    const handleOpenJISAAccount = useCallback(() => {
        navigateToIntroducingInvestmentAccount('JISA');
    }, [navigateToIntroducingInvestmentAccount]);
    // The floating bar starts a first investment, which needs a deposit, so web hides it
    const showInvestUsageExpandableFloatingBar = !isWeb && !walkthroughInProgress && !!secclAccounts?.length && !hasTradedInAnySecclAccount;
    const handleInvestUsageExpandableFloatingBarPress = useCallback(() => {
        if (secclAccounts?.length === 1) {
            dispatch(setSelectedTradingAccountId(secclAccounts?.[0]?.account.id));
            navigation.navigate('StartInvestChooseStrategy');
            return;
        }
        navigation.navigate('StartInvestChooseAccount');
    }, [secclAccounts]);
    const opacitySharedValue = useSharedValue(1);
    React.useEffect(() => {
        const unsubscribe = navigation.addListener('state', (e) => {
            const routeName = e.data.state.routes[e.data.state.index].name;
            if (routeName === 'Invest') {
                opacitySharedValue.value = withTiming(1, { duration: 200 });
            }
            else {
                opacitySharedValue.value = withTiming(0, { duration: 200 });
            }
        });
        return unsubscribe;
    }, [opacitySharedValue]);
    const opacityAnimatedStyle = useAnimatedStyle(() => ({
        opacity: opacitySharedValue.value,
    }));
    const expandableViewAnimatedProps = useAnimatedProps(() => ({
        pointerEvents: opacitySharedValue.value === 1 ? 'box-none' : 'none',
    }));
    const handleAddMoney = useCallback(() => {
        if (blockInvestActionOnWeb('Add money'))
            return;
        if (secclAccounts?.length) {
            navigation.navigate('AddMoneyToTradingAccount');
            return;
        }
        Modal.showConfirmation({
            title: 'You don’t have any active investment accounts',
            subtitle: 'Open an investment account first, then add your money to it',
            confirmButtonTitle: 'Open investment account',
            onConfirm: navigateToNextScreen,
        });
    }, [navigateToNextScreen, secclAccounts?.length]);
    return (<AppView withFrame={false}>
      <NavigationHeaderWithAvatar showTitle scrollY={scrollY} overlay={InvestHeaderOverlay}/>
      <Animated.ScrollView ref={scrollRef} scrollEventThrottle={16} onScroll={scrollHandler} onLayout={onScrollLayout} showsVerticalScrollIndicator={false} scrollEnabled={!walkthroughInProgress} onContentSizeChange={onContentSizeChange} contentContainerStyle={contentContainerStyle}>
        <TotalInvestmentsContainer onLayout={onTotalInvestmentsLayout}/>
        <Button hug brand small title="Add money" onPress={handleAddMoney} leftIconContainerStyle={styles.leftIconContainerStyle} leftIcon={<IconPlus width={rem(10)} height={rem(10)} color={colors.elements.white}/>}/>
        <Spacer h={32}/>
        <InitialInvestmentSection />
        {!!tradingAccountList?.length && (<>
            <View onLayout={onInvestmentAccountsLayout}>
              <SectionHeader horizontalMargins style={styles.accountsSectionHeader}>
                Your investment accounts
              </SectionHeader>
            </View>
            {tradingAccountList.map((account, index) => (<ActiveTradingAccountCard account={account} key={account.account.id || index} image={isAlpacaGIAAccountActive && account.account.accountType !== 'ISA'
                    ? getBrokerImage(account.account.broker)
                    : undefined}/>))}
          </>)}
        <Spacer h={8}/>
        <SectionHeader horizontalMargins style={styles.accountsSectionHeader}>
          Open an investment account
        </SectionHeader>
        {!isSecclISAOperating && (<ComingSoonTradingAccountCard title={AccountTypeDisplayLong.ISA} onPress={handleOpenSecclISAAccount} subTitle={AccountTypeDescription.ISA}/>)}
        {!isSecclGIAOperating && (<ComingSoonTradingAccountCard onPress={handleOpenSecclAccount} title={AccountTypeDisplayLong.GIA} subTitle={AccountTypeDescription.GIA} image={isAlpacaGIAAccountActive ? getBrokerImage('seccl') : undefined}/>)}
        <ComingSoonTradingAccountCard title={AccountTypeDisplayLong.JISA} onPress={handleOpenJISAAccount} subTitle={AccountTypeDescription.JISA}/>
        {/* <ComingSoonTradingAccountCard isComingSoon title="Personal pension" subTitle="Grow your retirement savings" /> */}
        <Spacer h={16}/>
        <FSCSCompensationInfoView />
        {!!showInvestUsageExpandableFloatingBar && <ExpandableFloatingBarSpacer isInTab/>}
        <InvestWalkthrough scrollRef={scrollRef} scrollHeight={scrollHeight} itemPositions={itemPositions} scrollLayoutHeight={scrollLayoutHeight}/>
      </Animated.ScrollView>
      {!!showInvestUsageExpandableFloatingBar && (<Portal hostName="BottomTabPortalHost">
          <Animated.View style={[StyleSheet.absoluteFillObject, opacityAnimatedStyle]} animatedProps={expandableViewAnimatedProps}>
            <InvestUsageExpandableFloatingBar onPress={handleInvestUsageExpandableFloatingBarPress} isInTab/>
          </Animated.View>
        </Portal>)}
      {!walkthroughInProgress && !!isAlpacaAccountActiveOrClosed && <MigrationToSecclBanner />}
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    accountsSectionHeader: {
        paddingTop: rem(16),
    },
    leftIconContainerStyle: {
        paddingRight: rem(8),
    },
}));
export default memo(MultiTradingAccountsView);
